import React, { Suspense, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import Header from "../../../assets/components/01_header/header";
import Footer from "../../../assets/components/02_footer/footer";

import BlogDashboardComponent from "../../../assets/components/03_blogdashboard/blogdashboardcomponent";
import BlogGridComponentMini from "../../../assets/components/05_bloggridmini/bloggridcomponentmini";


/*
|--------------------------------------------------------------------------
| Parse Markdown front matter
|--------------------------------------------------------------------------
*/

function parseMarkdownFile(markdown) {

    const frontMatterMatch = markdown.match(
        /^---\s*([\s\S]*?)\s*---\s*([\s\S]*)$/
    );

    if (!frontMatterMatch) {

        return {
            metadata: {},
            content: markdown
        };

    }

    const frontMatter = frontMatterMatch[1];

    const content = frontMatterMatch[2];

    const metadata = {};

    frontMatter
        .split(/\r?\n/)
        .forEach((line) => {

            const separator = line.indexOf(":");

            if (separator === -1) return;

            const key = line
                .substring(0, separator)
                .trim();

            let value = line
                .substring(separator + 1)
                .trim();

            /*
            |--------------------------------------------------------------------------
            | Remove surrounding quotes
            |--------------------------------------------------------------------------
            */

            if (
                (value.startsWith('"') && value.endsWith('"')) ||
                (value.startsWith("'") && value.endsWith("'"))
            ) {

                value = value.slice(1, -1);

            }

            metadata[key] = value;

        });

    return {
        metadata,
        content
    };

}


/*
|--------------------------------------------------------------------------
| Blog Dashboard
|--------------------------------------------------------------------------
*/

export default function BlogDetails({ params }) {

    const { id } = params;

    const [blog, setBlog] = useState(null);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | Load Markdown from Hostinger
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        async function loadBlog() {

            try {

                setLoading(true);

                setError(false);


                /*
                |--------------------------------------------------------------------------
                | IMPORTANT
                |--------------------------------------------------------------------------
                |
                | If id is:
                |
                | avoiding-google-penalty-seo-maintenance-tips-mumbai-businesses
                |
                | React requests:
                |
                | /blogs/avoiding-google-penalty-seo-maintenance-tips-mumbai-businesses.md
                |
                */

                const response = await fetch(
                    `/blogs/${encodeURIComponent(id)}.md`
                );


                if (!response.ok) {

                    throw new Error(
                        `Blog returned ${response.status}`
                    );

                }


                const markdown = await response.text();


                const parsed = parseMarkdownFile(markdown);


                setBlog(parsed);


            } catch (error) {

                console.error(
                    "Unable to load blog:",
                    error
                );

                setError(true);

            } finally {

                setLoading(false);

            }

        }


        if (id) {

            loadBlog();

        }

    }, [id]);


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <main>

                <Header />

                <div className="blog-content">

                    <div>
                        Loading blog...
                    </div>

                </div>

                <Footer />

            </main>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | Blog not found
    |--------------------------------------------------------------------------
    */

    if (error || !blog) {

        return (

            <main>

                <Header />

                <div className="blog-content">

                    <h1>
                        Blog not found
                    </h1>

                    <p>
                        The requested blog article
                        could not be found.
                    </p>

                </div>

                <Footer />

            </main>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | Metadata
    |--------------------------------------------------------------------------
    */

    const {
        title,
        meta_title,
        meta_description,
        description,
        author,
        date,
        modified,
        category,
        image
    } = blog.metadata;


    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (

        <main>

            <Header />


            <div className="blog-content">

                <article>


                    {/* Category */}

                    {category && (

                        <div className="blog-category">

                            {category}

                        </div>

                    )}


                    {/* Title */}

                    <h1>

                        {title || "Blog"}

                    </h1>


                    {/* Description */}

                    {(meta_description || description) && (

                        <p className="blog-description">

                            {meta_description || description}

                        </p>

                    )}


                    {/* Date / Author */}

                    {(author || date) && (

                        <div className="blog-meta">

                            {author && (

                                <span>
                                    By {author}
                                </span>

                            )}

                            {date && (

                                <span>
                                    {" "} · {date}
                                </span>

                            )}

                        </div>

                    )}


                    {/* Featured image */}

                    {image && (

                        <img
                            src={
                                image.startsWith("/")
                                    ? image
                                    : `/blogs/images/${image}`
                            }
                            alt={title || ""}
                            className="blog-featured-image"
                        />

                    )}


                    {/* Markdown */}

                    <div className="blog-markdown">

                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                        >

                            {blog.content}

                        </ReactMarkdown>

                    </div>


                </article>

            </div>


            <BlogDashboardComponent />

            <BlogGridComponentMini />


            <Footer />

        </main>

    );

}